import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {forkJoin, map, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import {AuthorizationServiceProvider} from 'src/app/auth_profile/services/authorization-service.provider';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import { ApiLessonFlag } from 'src/app/col/model/rest-model';
import {ColNativeV3Api} from 'src/app/col/services/api/v3/col-native-v3-api';
import {Dates, TimeUnits} from 'src/app/utils/calendar-utils';
import {ModalComponent} from 'src/app/utils/modal/modal.component';
import {SpinnerService} from 'src/app/utils/services/spinner.service';
import {NativeServiceApiProvider} from "../../../../services/native-api-provider.service";
import {Pageable} from "../../../../utils/pageable";
import {LessonProgressUtilsV3} from 'src/app/col/utils/v3/lesson-progress-utils';

@Component({
    selector: 'app-teacher-lesson-details',
    templateUrl: './teacher-lesson-details.component.html',
    styleUrls: ['./teacher-lesson-details.component.scss'],
    standalone: false
})
export class TeacherLessonDetailsComponent implements OnInit, OnDestroy {
  private static createEmptyMessage(): LessonsV3.ApiLessonMessage {
    return {
      lessonComment: '',
      privateLessonComment: '',
      message: '',
    };
  }
  private readonly plannedStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Initializing,
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InProgress,
  ];
  private readonly comingStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Initializing,
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
  ];

  @ViewChild('cancelLessonModal', { static: true })
  cancelLessonModal: ModalComponent;
  @ViewChild('flagModal', { static: true })
  flagModal: ModalComponent;

  lessonDetails: LessonsV3.LessonView;
  lessonMessage: LessonsV3.ApiLessonMessage = TeacherLessonDetailsComponent.createEmptyMessage();
  lessonProduct: LessonsV3.V3Product | null = null;
  schoolId: number;
  teacherId: number;
  studentId: number;
  lessonId: number;
  canUpdateProgress: boolean;
  _clReason: string;
  readonly cancelReasonMaxLength = 250;
  _fFlags: { [name: string]: boolean };
  _fDescription: string;
  _fExistingFlags: string[];
  _fAllFlags = ApiLessonFlag.flagsTypes;
  lessonFlags: LessonsV3.ApiLessonFlag[] = [];
  mayBeStartedTime: boolean;
  shouldBeStarted: boolean;
  lessonCommittedProgress: LessonsV3.CallanProgressDetails = {
    start: null,
    reading: null,
    nwp: null,
    headwords: '',
    productVersion: null,
  };
  currentProgress: LessonsV3.CallanProgressDetails | null = null;
  private hasCommittedProgress = false;
  private initSubscription: Subscription | null = null;

  constructor(
    private auth: AuthorizationServiceProvider,
    private route: ActivatedRoute,
    private provider: NativeServiceApiProvider,
    private spinner: SpinnerService
  ) {}

  ngOnInit(): void {
    this.initSubscription = this.auth
      .getAuthDetailsService()
      .pipe(
        switchMap((authDetailsApi) =>
          forkJoin({
            teacherId: authDetailsApi.getSelfTeacherId(),
            schoolId: authDetailsApi.getSelfSchoolId(),
          })
        ),
        tap(({teacherId, schoolId}) => {
          this.teacherId = teacherId;
          this.schoolId = schoolId;
        }),
        switchMap((_) => this.route.paramMap),
        tap((params) => {
          this.studentId = +params.get('studentId');
          this.lessonId = +params.get('lessonId');
        }),
        switchMap(() => this.loadLessonDetails())
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.initSubscription?.unsubscribe();
    this.initSubscription = null;
  }

  loadLessonDetails(): Observable<LessonsV3.CallanProgressDetails | null> {
    return this.spinner.trace<LessonsV3.CallanProgressDetails | null>(
      this.provider
        .colV3()
        .pipe(
          switchMap((api) =>
            api.getLesson(this.lessonId, 'teacher-preview').pipe(
              switchMap((lesson) =>
                forkJoin({
                  lesson: of(lesson),
                  teacherComment: api
                    .getLessonComment(this.lessonId, null)
                    .pipe(map((message) => message ?? TeacherLessonDetailsComponent.createEmptyMessage())),
                  studentComment: this.studentId
                    ? api
                        .getLessonComment(this.lessonId, this.studentId)
                        .pipe(map((message) => message ?? TeacherLessonDetailsComponent.createEmptyMessage()))
                    : of(TeacherLessonDetailsComponent.createEmptyMessage()),
                  flags: api.getLessonFlags(this.lessonId).pipe(
                    map((flags) => flags ?? [])
                  ),
                  product: this.resolveCourseProduct(
                    api,
                    (lesson as LessonsV3.LFTeacherPreview).course?.code
                  ),
                })
              )
            )
          ),
          tap(({teacherComment, studentComment, flags, product}) => {
            this.lessonMessage = {
              lessonComment: teacherComment.lessonComment,
              privateLessonComment: teacherComment.privateLessonComment,
              message: studentComment.message,
            };
            this.lessonFlags = flags;
            this.lessonProduct = product;
          }),
          map(({lesson}) => lesson),
          tap((lessonDetails) => this.setLessonDetails(lessonDetails)),
          switchMap((_) => this.loadProgressData())
        )
    );
  }

  private resolveCourseProduct(
    api: ColNativeV3Api,
    courseCode: string | null | undefined
  ): Observable<LessonsV3.V3Product | null> {
    if (!this.schoolId || !courseCode) {
      return of(null);
    }
    return api.getSchoolProducts(this.schoolId).pipe(
      switchMap((products: LessonsV3.V3Product[]) =>
        forkJoin(
          products.map((product) =>
            api.getProductCourses(this.schoolId, product.code).pipe(
              map((courses: LessonsV3.Course[]) => ({
                product,
                hasCourse: courses.some((course) => course.code === courseCode),
              }))
            )
          )
        )
      ),
      map(
        (matches: Array<{product: LessonsV3.V3Product; hasCourse: boolean}>) =>
          matches.find((entry) => entry.hasCourse)?.product ?? null
      )
    );
  }

  private setLessonDetails(lessonDetails: LessonsV3.LessonView) {
    type LegacyCompat = LessonsV3.LessonView & {
      message?: LessonsV3.ApiLessonMessage;
      studentMessage?: string;
      lessonStatus?: LessonsV3.LessonStatus | string;
      lessonType?: LessonsV3.LessonType | string;
      course?: LessonsV3.CourseRef & {
        product?: LessonsV3.V3Product;
      };
    };

    const compatLesson = lessonDetails as LegacyCompat;
    compatLesson.message =
      this.lessonMessage ?? TeacherLessonDetailsComponent.createEmptyMessage();
    compatLesson.studentMessage = this.lessonMessage?.message ?? '';
    compatLesson.lessonStatus = compatLesson.status ?? compatLesson.lessonStatus;
    compatLesson.lessonType = compatLesson.type ?? compatLesson.lessonType;
    if (compatLesson.course) {
      compatLesson.course.product = {
        code: this.lessonProduct?.code ?? '',
        name: this.lessonProduct?.name ?? this.lessonProduct?.code ?? '',
      };
    }

    this.lessonDetails = lessonDetails;
    this.lessonCommittedProgress = this.extractCommittedProgress(lessonDetails);
    this.hasCommittedProgress = this.extractProgressValues(
      (lessonDetails as Partial<LessonsV3.LFPreviewWithFullProgress>).progressCommited ?? null
    ).hasAny;
    const lessonStatus = this.lessonDetails.status as LessonsV3.LessonStatus;
    const isComing = this.comingStatuses.includes(lessonStatus);
    let plannedLessonStart = new Date(
      lessonDetails.lessonMetric.plannedStartDate
    );
    this.mayBeStartedTime =
      Dates.diff(new Date(), plannedLessonStart) <
      TimeUnits.Minutes(15).toMilis();
    this.shouldBeStarted =
      plannedLessonStart.getTime() < new Date().getTime() && isComing;
  }

  private extractCommittedProgress(
    lessonDetails: LessonsV3.LessonView
  ): LessonsV3.CallanProgressDetails {
    const committedBase = (lessonDetails as Partial<LessonsV3.LFPreviewWithFullProgress>)
      .progressCommited;
    const committed =
      (committedBase as Partial<LessonsV3.CallanProgressDetails> | undefined) ??
      undefined;
    return {
      start: committed?.start ?? 0,
      nwp: committed?.nwp ?? 0,
      reading: committed?.reading ?? 0,
      headwords: committed?.headwords ?? '',
      productVersion: committed?.productVersion ?? null,
    };
  }

  reloadLessonDetails() {
    this.loadLessonDetails().subscribe();
  }

  cancelLesson() {
    let cancellationObservable: Observable<LessonsV3.LessonView | void> | null = null;
    if (
      (this.lessonDetails.status === 'Booked' ||
        this.lessonDetails.status === 'Initializing') &&
      !this.mayBeStartedTime
    ) {
      cancellationObservable = this.provider.colV3().pipe(
        switchMap((api) => api.updateLessonStatus(this.lessonId, LessonsV3.LessonStatusAction.Cancel, { reason: this._clReason }, null, this.teacherId))
      );
    } else if (
      this.lessonDetails.status === 'Due' ||
      this.lessonDetails.status === 'Booked'
    ) {
      cancellationObservable = this.provider.colV3().pipe(
        switchMap((api) => api.updateLessonStatus(this.lessonId, LessonsV3.LessonStatusAction.Invalidate, { reason: this._clReason }, null, this.teacherId))
      );
    }

    if (cancellationObservable) {
      cancellationObservable
        .pipe(switchMap(() => this.loadLessonDetails()))
        .subscribe();
    }

    this.cancelLessonModal.hide();
  }

  showCancelLessonModal() {
    this._clReason = null;
    this.cancelLessonModal.show();
  }

  loadProgressData(): Observable<LessonsV3.CallanProgressDetails | null> {
    const productCode = this.lessonProduct?.code ?? null;
    if (
      !this.plannedStatuses.includes(
        this.lessonDetails.status as LessonsV3.LessonStatus
      ) ||
      !productCode
    ) {
      this.currentProgress = null;
      return of(null);
    }
    return this.provider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.findLessons(
            LessonsV3.LessonCategory.Col,
            null,
            'preview-progress',
            null,
            this.studentId,
            null,
            null,
            null,
            productCode,
            null,
            null,
            null,
            new Pageable(0, 50, ['metric.details.plannedStartDate,DESC'])
          )
        ),
        map((page) => page?.content ?? []),
        map((lessons) =>
          lessons
            .map((lesson) => ({
              rawProgress:
                (lesson as LessonsV3.LFPreviewWithFullProgress).progressCommited ??
                null,
              courseCode: lesson.course?.code ?? this.lessonDetails.course.code,
              productCode,
            }))
            .map((entry) => ({
              ...entry,
              progress: this.extractProgressValues(entry.rawProgress),
            }))
            .find((entry) => entry.progress.hasAny)
        ),
        // create new if is first lesson
        map((currentProductProgress) => {
          if (currentProductProgress) {
            return {
              start: currentProductProgress.progress.start,
              nwp: currentProductProgress.progress.nwp,
              reading: currentProductProgress.progress.reading,
              headwords: currentProductProgress.progress.headwords,
              productVersion: null,
            } as LessonsV3.CallanProgressDetails;
          }
          return {
            start: 0,
            nwp: 0,
            reading: 0,
            headwords: '',
            productVersion: null,
          } as LessonsV3.CallanProgressDetails;
        }),
        // keep it
        tap((currentProgress) => {
          this.currentProgress = currentProgress;
          // lesson has no committed progress yet — prefill the commit form with
          // the same proposal the header shows
          if (!this.hasCommittedProgress && currentProgress) {
            this.lessonCommittedProgress =
              LessonProgressUtilsV3.buildProposedProgress(currentProgress);
          }
        })
      );
  }

  private extractProgressValues(
    progress: LessonsV3.ProgressDetailsBase | null
  ): { start: number; nwp: number; reading: number; headwords: string; hasAny: boolean } {
    const p = (progress ?? {}) as Partial<LessonsV3.CallanProgressDetails> & {
      hw?: string | null;
    };
    const start = p.start ?? 0;
    const nwp = p.nwp ?? 0;
    const reading = p.reading ?? 0;
    const headwords = (p.hw ?? p.headwords ?? '') as string;
    const hasAny =
      p.start != null ||
      p.nwp != null ||
      p.reading != null ||
      p.hw != null ||
      p.headwords != null;

    return { start, nwp, reading, headwords, hasAny };
  }

  //flag modal
  showFlagsModal() {
    this._fFlags = {};
    this._fDescription = null;
    const flags = this.lessonFlags ?? [];
    this._fExistingFlags = flags.map((f) => f.flagType);
    this._fExistingFlags.forEach((f) => (this._fFlags[f] = true));
    this.flagModal.show();
  }

  hasFlag(type: string) {
    return this._fExistingFlags.some((f) => f === type);
  }

  getFlagDescription(type: string) {
    return ApiLessonFlag.getFLagDescription(type);
  }

  postFlag() {
    let postObservable: Observable<LessonsV3.ApiLessonFlag | null> = of(null);
    for (const flagType in this._fFlags) {
      if (this._fFlags.hasOwnProperty(flagType)) {
        if (
          !this._fFlags[flagType] ||
          this._fExistingFlags.indexOf(flagType) >= 0
        ) {
          continue;
        }
        const description = ApiLessonFlag.descriptionRequiringFlags.some((f) => f === flagType)
          ? this._fDescription
          : undefined;
        const flagDto: LessonsV3.LessonFlagDto = {
          flagType: flagType as LessonsV3.LessonFlagType,
          description,
          status: LessonsV3.LessonFlagStatus.Submitted,
        };
        postObservable = postObservable.pipe(
          switchMap(() =>
            this.provider.colV3().pipe(
              switchMap((api) => api.createLessonFlag(this.lessonDetails.id, flagDto, null, this.teacherId))
            )
          )
        );
      }
    }
    postObservable.pipe(switchMap(() => this.loadLessonDetails())).subscribe();
    this.flagModal.hide();
  }

  askForComment(): boolean {
    let res = false;
    for (const key in this._fFlags) {
      if (this._fFlags.hasOwnProperty(key)) {
        if (
          this._fFlags[key] &&
          ApiLessonFlag.descriptionRequiringFlags.some((f) => f === key)
        ) {
          res = true;
          break;
        }
      }
    }
    return res;
  }
}
